import styled from '@emotion/styled';
import { Typography } from 'antd';
import React from 'react';
import type { SVGProps } from 'react';

import colors from 'config/theme/colors';

import FileTypeIcon from './Medias/FileTypeIcon';

const { Title } = Typography;

// ============================================================================
// Base Styled Components
// ============================================================================

export const List = styled('ul', { target: 'fb-media-gallery-list' })`
  list-style: none;
  padding-left: 0;
  margin-bottom: 0;
`;

export const Time = styled('li', { target: 'fb-media-gallery-time' })`
  font-size: 12px;
  letter-spacing: -0.3px;
  color: ${colors['@text-color-secondary']};
  white-space: nowrap;
`;

export const MediaThumbnail = styled('div', { target: 'fb-media-gallery-media-thumbnail' })`
  height: 83px;
  width: auto;
  flex-shrink: 0;
  background-color: ${colors['@body-background']};
  color: ${colors['@primary-color']};
`;

// ============================================================================
// Components depending on base components
// ============================================================================

export const MediaActionsFooter = styled('div', { target: 'fb-media-gallery-media-actions-footer' })`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: center;
  padding: 4px;
  height: 100%;

  ${List} {
    max-width: 80%;
    display: flex;
    align-items: center;
  }

  ${Time} {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const ItemWrapper = styled(MediaThumbnail, { target: 'fb-media-gallery-item-wrapper' })`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 53px;
  border-radius: 5px 5px 0 0;

  svg {
    height: 45px;
  }
`;

// ============================================================================
// Media Container Components
// ============================================================================

export const MediaWrapper = styled('div', { target: 'fb-media-gallery-media-wrapper' })`
  position: relative;
  background-color: ${colors['@white']};
  border: 1px solid ${colors['@border-color-base']};
  border-radius: 6px;
  height: 121px;
  width: 160px;
  margin-right: 16px;
  margin-top: 1rem;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

export const VideoWrapper = styled('div', { target: 'fb-media-gallery-video-wrapper' })`
  height: 83px;
  width: auto;
  flex-shrink: 0;
  cursor: pointer;
  overflow: hidden;
  position: relative;
  background-color: ${colors['@video-bg']};
`;

export const VideoThumbnail = styled('video', { target: 'fb-media-gallery-video-thumbnail' })`
  width: 100%;
  height: 100%;

  color: ${colors['@primary-color']};
  background-color: ${colors['@item-hover-bg']};
  object-fit: cover;
  border-radius: 4px 4px 0 0;

  &::before {
    content: '';
    display: block;
    padding-bottom: 56.25%;
  }
`;

export const ProcessingWrapper = styled.div`
  height: 100%;
  cursor: initial;

  > svg {
    height: 40px;
    width: 40px;
    fill: ${colors['@primary-color']};
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &.error {
    background-color: ${colors['@error-color-background']};

    > svg {
      fill: ${colors['@error-color']};
    }
  }
`;

export const PlayIconWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(7px);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  i {
    font-size: 24px;
    color: ${colors['@text-color']};
    margin-left: 2px;
  }
`;

type ImageThumbnailWrapperProps = {
  background?: string;
};

export const ImageThumbnailWrapper = styled(MediaThumbnail, { target: 'fb-media-gallery-image-thumbnail-wrapper' })<ImageThumbnailWrapperProps>`
  width: auto;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  background-image: url("${({ background }) => background}");
  cursor: pointer;
  position: relative;
  border-radius: 4px 4px 0 0;

  &::before {
    content: '';
    display: block;
    padding-bottom: 56.25%;
  }
`;

// ============================================================================
// Icon Components
// ============================================================================

export const DeleteIcon = styled.span`
  top: 4px;
  right: 4px;
  z-index: 2;
  cursor: pointer;
  font-size: 18px;
  position: absolute;
  border-radius: 50%;
  background-color: ${colors['@white']};
`;

export const RestrictIcon = styled.i`
  width: 22px;
  height: 22px;
  top: 6px;
  right: 6px;
  background-color: ${colors['@primary-color-darker']};

  &.private {
    background-color: ${colors['@warning-color']};
  }
`;

export const Badge360 = styled.i`
  position: absolute;
  margin-right: 0;
  top: 4px;
  left: 4px;
  color: ${colors['@white']};
  font-size: 20px;
`;

export const MediaTypeIcon = styled.i`
  font-size: 16px;
  color: ${colors['@primary-color']};
`;

export const FilenameWrapper = styled.span`
  display: flex;
  font-size: 12px;
  overflow: hidden;
  cursor: default;

  > span {
    cursor: default;
    white-space: nowrap;
    &:first-child {
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &:last-child {
      flex-shrink: 0;
    }
  }
`;

// ============================================================================
// List Components
// ============================================================================

export const StyledTitle = styled(Title, { target: 'fb-media-gallery-styled-title' })`
  &.ant-typography {
    text-transform: capitalize !important;
    margin-top: 16px;
    font-weight: 500;
  }
`;

export const GroupedList = styled.div`
  ${StyledTitle}:first-child {
    margin-top: 0;
  }

  .media-list {
    border-bottom: solid 1px ${colors['@disabled-bg']};

    .media-item {
      margin-top: 0 !important;
      margin-bottom: 16px !important;
    }

    &:last-child {
      border-bottom: none;
    }
  }
`;

export const MediaListWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;

  &.thumbnail {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;

    &.uploader-gallery {
      ${MediaWrapper}:only-child {
        width: 60px;
        height: 60px;
      }
    }

    ${MediaWrapper} {
      width: 60px;
      height: 60px;
      border-radius: 8px;
      margin: 0;
      box-shadow: 0px -4px 6px 0px rgba(106, 106, 110, 0.1);

      ${VideoWrapper} {
        width: 100%;
        height: 100%;
        max-width: 150px;
        max-height: 150px;
        border-radius: 8px;
      }

      &:only-child {
        width: 150px;
        height: 150px;

        ${MediaThumbnail}, ${VideoWrapper}, ${ItemWrapper}, ${ImageThumbnailWrapper} {
          width: 100%;
          height: 100%;
          max-width: 150px;
          max-height: 150px;
        }
      }

      ${MediaThumbnail}, ${ItemWrapper}, ${ImageThumbnailWrapper} {
        height: 60px;
        font-size: 60px;
        border-radius: 8px;
      }

      ${MediaActionsFooter} {
        display: none;
      }
    }
  }

  &.small {
    ${MediaWrapper} {
      width: 103px;
      height: 75px;

      ${MediaThumbnail}, ${ItemWrapper}, ${ImageThumbnailWrapper} {
        height: 54px;
        font-size: 27px;
      }

      ${MediaActionsFooter} {
        i[class*='fb-icon-'],
        .anticon {
          font-size: 10px !important;
        }
      }
    }
  }
`;

// ============================================================================
// Functional Components
// ============================================================================

type DocumentIconProps = {
  extension?: string;
  theme?: string;
} & SVGProps<SVGSVGElement>;

export const DocumentIcon: React.FC<DocumentIconProps> = ({ extension, theme: _theme, ...iconProps }) => (
  <ItemWrapper>
    <FileTypeIcon extension={extension} {...iconProps} />
  </ItemWrapper>
);
